import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiBox, FiDollarSign, FiCalendar, FiX, FiSave, FiTruck, FiBarChart2 } = FiIcons;

const PalletManager = () => {
  const { pallets, addPallet, updatePallet, deletePallet } = useInventory();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingPallet, setEditingPallet] = useState(null);
  const [formData, setFormData] = useState({
    pallet_number: '',
    source: '',
    cost: '',
    purchase_date: new Date().toISOString().split('T')[0],
    description: '',
    is_active: true
  });

  const handleOpenModal = (pallet = null) => {
    if (pallet) {
      setEditingPallet(pallet);
      setFormData({
        pallet_number: pallet.pallet_number,
        source: pallet.source || '',
        cost: pallet.cost,
        purchase_date: pallet.purchase_date,
        description: pallet.description || '',
        is_active: pallet.is_active
      });
    } else {
      setEditingPallet(null);
      setFormData({
        pallet_number: '',
        source: '',
        cost: '',
        purchase_date: new Date().toISOString().split('T')[0],
        description: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingPallet) {
      await updatePallet(editingPallet.id, formData);
    } else {
      await addPallet(formData);
    }
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Pallet Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Track pallets, costs, and organize inventory sources.</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/pallets/report')}
            className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <SafeIcon icon={FiBarChart2} className="mr-2 h-5 w-5" />
            Profitability Report
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
            Add Pallet
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pallets.map((pallet) => (
          <motion.div
            key={pallet.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${pallet.is_active ? 'border-blue-200 dark:border-blue-800' : 'border-gray-200 dark:border-gray-700'} p-6`}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${pallet.is_active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                  <SafeIcon icon={FiBox} className="h-6 w-6" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{pallet.pallet_number}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${pallet.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'}`}>
                    {pallet.is_active ? 'Active' : 'Closed'}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleOpenModal(pallet)} className="text-gray-400 hover:text-blue-600">
                  <SafeIcon icon={FiEdit2} className="h-4 w-4" />
                </button>
                <button onClick={() => deletePallet(pallet.id)} className="text-gray-400 hover:text-red-600">
                  <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center text-gray-600 dark:text-gray-400 pb-2 border-b border-gray-100 dark:border-gray-700">
                <span className="flex items-center"><SafeIcon icon={FiTruck} className="mr-2 h-4 w-4 text-gray-400" /> Purchased From:</span>
                <span className="font-medium text-gray-900 dark:text-white">{pallet.source || 'N/A'}</span>
              </div>
              
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span className="flex items-center"><SafeIcon icon={FiDollarSign} className="mr-2 h-4 w-4 text-gray-400" /> Cost:</span>
                <span className="font-semibold text-gray-900 dark:text-white">${parseFloat(pallet.cost).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span className="flex items-center"><SafeIcon icon={FiCalendar} className="mr-2 h-4 w-4 text-gray-400" /> Date:</span>
                <span>{new Date(pallet.purchase_date).toLocaleDateString()}</span>
              </div>
            </div>
            {pallet.description && (
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-xs text-gray-600 dark:text-gray-400">
                {pallet.description}
              </div>
            )}
          </motion.div>
        ))}
        
        {pallets.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center p-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
            <SafeIcon icon={FiBox} className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Pallets Found</h3>
            <p className="text-gray-500 dark:text-gray-400 text-center max-w-sm mt-2 mb-6">
              Create a pallet to categorize your inventory and track profitability by shipment.
            </p>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
              Add Your First Pallet
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingPallet ? 'Edit Pallet' : 'Add New Pallet'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pallet Number / ID</label>
                  <input
                    type="text"
                    required
                    value={formData.pallet_number}
                    onChange={e => setFormData({ ...formData, pallet_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="e.g. PAL-101"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purchased From (Source)</label>
                  <input
                    type="text"
                    required
                    value={formData.source}
                    onChange={e => setFormData({ ...formData, source: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="e.g. Target, Liquidation.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.cost}
                      onChange={e => setFormData({ ...formData, cost: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purchase Date</label>
                    <input
                      type="date"
                      required
                      value={formData.purchase_date}
                      onChange={e => setFormData({ ...formData, purchase_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Optional notes about contents..."
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active Pallet (Open for scanning)</label>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <SafeIcon icon={FiSave} className="mr-2 h-4 w-4" />
                    Save Pallet
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PalletManager;